import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import {
    Box,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    useDisclosure,
    Text,
    DrawerCloseButton,

    /* Hooks */
    useBreakpointValue,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

/* Styles */
import { useMyRewardsStyles } from '../styles/myRewardsStyles'

const ExclusiveRewardsBlock = React.forwardRef(({ loyaltyData, isLoyaltyDataLoading }, ref) => {
    // This will fallback CMS
    const hardCodedData = {
        title: 'Enjoy Your Exclusive <span class="highlighted">Rewards</span>',
        subTitle: "And just like that... thanks to your points and your tier status, you're already earning rewards. Don't miss out!",
        backgroundImage: {
            mobileSrc: 'static/img/my-rewards/exclusive-rewards-bg-mobile.png',
            desktopSrc: 'static/img/my-rewards/exclusive-rewards-bg.jpg'
        }
    }

    const styles = useMyRewardsStyles()
    const isMobile = useBreakpointValue({ base: true, lg: false })
    const erbStyling = styles?.components?.exclusiveRewardsBlock
    const rewards = loyaltyData?.rewards
    const rewardsDataMapping = loyaltyData?.rewardsDataMapping

    // Refs and state for popup handling
    const containerRef = useRef(null)
    const { isOpen, onOpen, onClose } = useDisclosure()
    const [popupContent, setPopupContent] = useState('')

    const handlePopupClick = (event) => {
        event.preventDefault()
        const trigger = event.target.closest('[data-popup-trigger]')

        if (!trigger) return

        const tileWrapper = trigger.closest('[data-tile-index]')

        if (!tileWrapper) return

        const tileIndex = parseInt(tileWrapper.getAttribute('data-tile-index'))
        const reward = rewards?.[tileIndex]

        if (reward) {
            const drawerContent = rewardsDataMapping[reward['popup-content-id']]
            if (drawerContent) {
                setPopupContent(drawerContent)
                onOpen()
            }
        }
    }

    // Event delegation for popup triggers
    useEffect(() => {
        const container = containerRef.current
        if (container) {
            container.addEventListener('click', handlePopupClick)
            return () => {
                container.removeEventListener('click', handlePopupClick)
            }
        }
    }, [rewards, rewardsDataMapping, onOpen])

    return (
        <Box ref={ref}>
            <Box {...erbStyling.container}
                ref={containerRef}
                sx={{
                    backgroundImage: `url(${isMobile ? getAssetUrl(hardCodedData.backgroundImage.mobileSrc) : getAssetUrl(hardCodedData.backgroundImage.desktopSrc)})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}>
                <Box
                    {...styles.sectionTitle}
                    sx={styles.highlighted}
                    dangerouslySetInnerHTML={{ __html: hardCodedData.title }} />
                <Text  {...styles.sectionDescription}  {...erbStyling.sectionDescription}>
                    {hardCodedData.subTitle}
                </Text>
                <Skeleton isLoaded={!isLoyaltyDataLoading} {...erbStyling.tiles} {...styles.componentWrapper}>
                    {rewards?.map((reward, index) => {
                        const content = rewardsDataMapping && rewardsDataMapping[reward['tile-content-id']]
                        return (content &&
                            <Box
                                key={index}
                                sx={erbStyling.tileWrapper}
                                data-tile-index={index}
                                dangerouslySetInnerHTML={{ __html: content }}
                            />
                        )
                    })}
                </Skeleton>
            </Box>

            <Drawer
                placement="right"
                isOpen={isOpen}
                onClose={onClose}
                onOpen={onOpen}
                size="md"
                scrollBehavior="inside"
                isFullHeight={true}
            >
                <DrawerOverlay />
                <DrawerContent {...styles.drawerContent}>
                    <DrawerCloseButton />
                    <DrawerBody {...styles.drawerBody}>
                        <Box sx={styles.drawerWrapper}
                            dangerouslySetInnerHTML={{ __html: popupContent }} />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    )
})

ExclusiveRewardsBlock.propTypes = {
    loyaltyData: PropTypes.object,
    isLoyaltyDataLoading: PropTypes.bool
}

export default ExclusiveRewardsBlock
